import type { Atom, Getter, Setter, WritableAtom } from 'jotai';

/** What every snapshot carries, whatever else it holds. */
export interface LoadState {
    isLoading: boolean;
    error: Error | null;
}

/** The snapshot of one item's settings: null until they load. */
export interface SettingsSnapshot<T> extends LoadState {
    settings: T | null;
}

/** The settings that a form edits, with the commands that the form gives. */
export interface EditedSettings<T> extends SettingsSnapshot<T> {
    /** Loads the settings again at once: the Retry of a failed load. */
    retry: () => void;
    /** Puts the answer of the form's own write on screen. */
    answer: (settings: T) => void;
}

/** What a container asks of one id's snapshot in an api atom family. */
export enum SnapshotCommand {
    /** Load again, after the resource's delay when it has one. */
    Reload = 'reload',
    /** Load again at once, as a retry does. */
    ReloadNow = 'reloadNow',
    /** Put a write's answer on screen. */
    Answer = 'answer',
}

export type SnapshotWrite<S> =
    { command: SnapshotCommand.Reload } |
    { command: SnapshotCommand.ReloadNow } |
    { command: SnapshotCommand.Answer; snapshot: S };

/** What a container asks of a query member, beside the commands of each snapshot. */
export enum QueryCommand {
    /** Load the older page. */
    LoadOlder = 'loadOlder',
    /** Patch the held rows with a change, or read them again. */
    Apply = 'apply',
}

export type QueryWrite<S, C = never> =
    SnapshotWrite<S> |
    { command: QueryCommand.LoadOlder } |
    { command: QueryCommand.Apply; change: C };

/** How `apply` took a change. */
export enum ApplyOutcome {
    /** The held rows took the change. */
    Patched,
    /** The held rows cannot take it: load them again. */
    Reread,
    /** The held rows are no longer true: drop them and load as the first load does. */
    Reset,
}

export type ApplyResult<S> =
    { outcome: ApplyOutcome.Patched; snapshot: S } |
    { outcome: ApplyOutcome.Reread } |
    { outcome: ApplyOutcome.Reset };

type Reload = WritableAtom<null, [], void>;

export interface SnapshotAtoms<S> {
    /** Read this. Always a settled snapshot. */
    value: Atom<S>;
    /** Set this to load again, after `reloadAfterMs` when the resource sets it. */
    reload: Reload;
    /** Set this to load again at once, as a retry does. */
    reloadNow: Reload;
    /** Set this to load again, unless a load is in flight or settled moments ago. */
    revalidate: Reload;
    /** Set this with a write's answer: it is newer than any load in flight. */
    answer: WritableAtom<null, [S], void>;
}

export interface Options<S> {
    /** Reload on this interval for as long as something reads the snapshot. */
    refreshEveryMs?: number;
    /** `reload` waits this long, and every `reload` inside the wait is answered by its load. */
    reloadAfterMs?: number;
    /** Runs after every load that succeeded, with the time that load started. */
    onLoaded?: (snapshot: S, startedAtMs: number, get: Getter, set: Setter) => void;
    /**
     * A reader that comes back reads as loading until its load answers, for a reader that acts
     * on the data at once, such as the seed of a search.
     */
    isStaleWhenUnread?: boolean;
}

export interface PagedOptions<S, P> extends Options<S> {
    /** The older page, from the cursor that the held rows give. */
    loadOlder?: (signal: AbortSignal, previousSnapshot: S) => Promise<P>;
}

/** Where the older page of a feed is. */
export enum OlderState {
    Idle,
    Loading,
    /** The last older page failed; the held rows are as they were before it. */
    Failed,
}

export interface PagedSnapshotAtoms<S> extends SnapshotAtoms<S> {
    /** Set this to load the older page. */
    olderReload: Reload;
    /** Read this before `olderReload`: a caller sends it only while this is not `Loading`. */
    olderState: Atom<OlderState>;
    /** The poll tick: a refresh, skipped while any load is in flight. */
    tick: Reload;
    /** Set this with a change's patch for the held rows, or a reread. */
    apply: WritableAtom<null, [(previousSnapshot: S) => ApplyResult<S>], void>;
}
